import * as THREE from 'three';
import { BASE_COL, BASE_COLS, BASE_ROW, BASE_ROWS, CELL_SIZE, ENEMY_BASE_RANGE, FIELD, GRID_COLS, GRID_ROWS } from './config';
import type { Enemy, Obstacle, PathCell } from './types';

const DIRECTIONS: readonly (readonly [number, number])[] = [[0, 1], [-1, 0], [1, 0], [0, -1]];
const UNVISITED = -2;
const NO_PARENT = -1;

export function cellToWorld(col: number, row: number): THREE.Vector3 {
  return new THREE.Vector3(FIELD.left + (col + 0.5) * CELL_SIZE, 0, FIELD.top + (row + 0.5) * CELL_SIZE);
}

export function worldToCell(x: number, z: number): PathCell {
  return {
    col: THREE.MathUtils.clamp(Math.floor((x - FIELD.left) / CELL_SIZE), 0, GRID_COLS - 1),
    row: THREE.MathUtils.clamp(Math.floor((z - FIELD.top) / CELL_SIZE), 0, GRID_ROWS - 1),
  };
}

function cellIndex(col: number, row: number): number {
  return row * GRID_COLS + col;
}

function cellCenterX(col: number): number {
  return FIELD.left + (col + 0.5) * CELL_SIZE;
}

function cellCenterZ(row: number): number {
  return FIELD.top + (row + 0.5) * CELL_SIZE;
}

export class NavigationSystem {
  private readonly basePoint = new THREE.Vector3(FIELD.baseX, 0, FIELD.baseZ);
  private readonly cellPoint = new THREE.Vector3();

  constructor(
    private readonly obstacles: Obstacle[],
    private readonly getPlayerCell: () => PathCell,
    private readonly isRiverCell: (col: number, row: number) => boolean = () => false,
    private readonly isBridgeCell: (col: number, row: number) => boolean = () => false,
  ) {}

  isCellBlocked(col: number, row: number): boolean {
    if (col < 0 || col >= GRID_COLS || row < 0 || row >= GRID_ROWS) return true;
    const baseCell = col >= BASE_COL && col < BASE_COL + BASE_COLS && row >= BASE_ROW && row < BASE_ROW + BASE_ROWS;
    const riverCell = this.isRiverCell(col, row);
    const bridgeCell = this.isBridgeCell(col, row);
    if (baseCell || (riverCell && !bridgeCell)) return true;
    for (const obstacle of this.obstacles) {
      if (obstacle.col === col && obstacle.row === row) return true;
    }
    return false;
  }

  obstacleAt(x: number, z: number, radius = 0): Obstacle | undefined {
    for (const obstacle of this.obstacles) {
      if (Math.abs(x - obstacle.x) < obstacle.halfWidth + radius
        && Math.abs(z - obstacle.z) < obstacle.halfDepth + radius) return obstacle;
    }
    return undefined;
  }

  findSafePlayerSpawn(): PathCell {
    const candidates: readonly (readonly [number, number])[] = [[7, 10], [6, 10], [8, 10], [7, 9], [5, 10], [9, 10]];
    for (const [col, row] of candidates) {
      if (!this.isCellBlocked(col, row)) return { col, row };
    }
    return { col: 8, row: 10 };
  }

  hasLineOfSight(from: THREE.Vector3, to: THREE.Vector3): boolean {
    const distance = Math.hypot(to.x - from.x, to.z - from.z);
    const steps = Math.ceil(distance / 0.3);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      if (this.obstacleAt(from.x + (to.x - from.x) * t, from.z + (to.z - from.z) * t, 0.08)) return false;
    }
    return true;
  }

  findPathToBase(enemy: Enemy): PathCell[] {
    const startIndex = cellIndex(enemy.gridCol, enemy.gridRow);
    const queue: PathCell[] = [{ col: enemy.gridCol, row: enemy.gridRow }];
    const previous = new Int16Array(GRID_COLS * GRID_ROWS);
    previous.fill(UNVISITED);
    previous[startIndex] = NO_PARENT;
    let queueIndex = 0;
    let bestIndex = startIndex;
    let bestDistanceSq = distanceToBaseSquared(enemy.gridCol, enemy.gridRow);

    while (queueIndex < queue.length) {
      const current = queue[queueIndex++];
      const currentIndex = cellIndex(current.col, current.row);
      const currentDistanceSq = distanceToBaseSquared(current.col, current.row);
      if (currentDistanceSq < bestDistanceSq) {
        bestDistanceSq = currentDistanceSq;
        bestIndex = currentIndex;
      }
      if (this.isBaseAttackCell(current.col, current.row)) {
        return this.reconstructPath(previous, startIndex, currentIndex);
      }

      for (const [dc, dr] of DIRECTIONS) {
        const col = current.col + dc;
        const row = current.row + dr;
        if (col < 0 || col >= GRID_COLS || row < 0 || row >= GRID_ROWS) continue;
        const nextIndex = cellIndex(col, row);
        if (previous[nextIndex] !== UNVISITED || !this.isEnemyWalkable(col, row, enemy)) continue;
        previous[nextIndex] = currentIndex;
        queue.push({ col, row });
      }
    }
    return this.reconstructPath(previous, startIndex, bestIndex);
  }

  /** Find a route to an arbitrary walkable cell, used for enemy scouting. */
  findPathToCell(enemy: Enemy, target: PathCell): PathCell[] {
    if (!this.isEnemyWalkable(target.col, target.row, enemy)) return [];
    const startIndex = cellIndex(enemy.gridCol, enemy.gridRow);
    const targetIndex = cellIndex(target.col, target.row);
    if (startIndex === targetIndex) return [];

    const queue: PathCell[] = [{ col: enemy.gridCol, row: enemy.gridRow }];
    const previous = new Int16Array(GRID_COLS * GRID_ROWS);
    previous.fill(UNVISITED);
    previous[startIndex] = NO_PARENT;
    let queueIndex = 0;

    while (queueIndex < queue.length) {
      const current = queue[queueIndex++];
      const currentIndex = cellIndex(current.col, current.row);
      if (currentIndex === targetIndex) return this.reconstructPath(previous, startIndex, currentIndex);

      for (const [dc, dr] of DIRECTIONS) {
        const col = current.col + dc;
        const row = current.row + dr;
        if (col < 0 || col >= GRID_COLS || row < 0 || row >= GRID_ROWS) continue;
        const nextIndex = cellIndex(col, row);
        if (previous[nextIndex] !== UNVISITED || !this.isEnemyWalkable(col, row, enemy)) continue;
        previous[nextIndex] = currentIndex;
        queue.push({ col, row });
      }
    }
    return [];
  }

  private isEnemyWalkable(col: number, row: number, enemy?: Enemy): boolean {
    let destructibleObstacle = false;
    for (const obstacle of this.obstacles) {
      if (obstacle.col === col && obstacle.row === row && obstacle.destructible) {
        destructibleObstacle = true;
        break;
      }
    }
    const bulldozerCanBreak = enemy?.type === 'bulldozer' && destructibleObstacle;
    if (this.isCellBlocked(col, row) && !bulldozerCanBreak) return false;
    const playerCell = this.getPlayerCell();
    return !(col === playerCell.col && row === playerCell.row);
  }

  private isBaseAttackCell(col: number, row: number): boolean {
    if (this.isCellBlocked(col, row)) return false;
    this.cellPoint.set(cellCenterX(col), 0, cellCenterZ(row));
    return this.cellPoint.distanceToSquared(this.basePoint) <= ENEMY_BASE_RANGE * ENEMY_BASE_RANGE
      && this.hasLineOfSight(this.cellPoint, this.basePoint);
  }

  private reconstructPath(previous: Int16Array, startIndex: number, endIndex: number): PathCell[] {
    const path: PathCell[] = [];
    let currentIndex = endIndex;
    while (currentIndex !== startIndex && currentIndex >= 0) {
      const row = Math.floor(currentIndex / GRID_COLS);
      path.push({ col: currentIndex - row * GRID_COLS, row });
      currentIndex = previous[currentIndex];
    }
    return path.reverse();
  }
}

function distanceToBaseSquared(col: number, row: number): number {
  const deltaX = cellCenterX(col) - FIELD.baseX;
  const deltaZ = cellCenterZ(row) - FIELD.baseZ;
  return deltaX * deltaX + deltaZ * deltaZ;
}
